import { ReactNode, useCallback, useMemo, useState } from 'react';

import { Box, Button, styled } from '@mui/material';

import { ActionLabels } from '@sbv-clients/util-misc';

import {
  ConfirmDialogButtons,
  Dialog,
  DialogActionsContainer,
  DialogContent,
  DialogHeader,
  DialogMessage,
  DialogMessageTone,
  smallDialogBodySx,
  smallDialogSx,
  useRedesignDialogs,
} from '../../components';
import useToggleModal from '../use-toggle-modal/use-toggle-modal';

const PlainMessage = styled(Box)(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '21px',
  color: theme.custom?.stepText,
}));

export type ConfirmationModalConfig = {
  title: string;
  content: ReactNode;
  // New design only (Employee Center): icon tone, icon and button text.
  tone?: DialogMessageTone;
  // 'none': just the message, no icon.
  icon?: DialogMessageTone | 'lock' | 'none';
  confirmLabel?: string;
  danger?: boolean;
};

// `redesign` opts in to the new dialog look outside the Employee Center.
export const useConfirmationModal = (options?: { redesign?: boolean }) => {
  const redesign = useRedesignDialogs() || !!options?.redesign;
  const { open, openModal, closeModal } = useToggleModal();
  const [modalConfig, setModalConfig] =
    useState<ConfirmationModalConfig | null>(null);
  const [resolveReject, setResolveReject] = useState<{
    resolve: (() => void) | null;
    reject: (() => void) | null;
  }>({ resolve: null, reject: null });

  const onCancelAction = useCallback(() => {
    resolveReject.reject?.();
    closeModal();
  }, [resolveReject, closeModal]);

  const handleOkAction = useCallback(() => {
    resolveReject.resolve?.();
    closeModal();
  }, [resolveReject, closeModal]);

  const modal = useMemo(() => {
    if (!modalConfig) return null;

    if (redesign) {
      return (
        <Dialog open={open} fullWidth sx={smallDialogSx}>
          <DialogHeader title={modalConfig.title} onClose={onCancelAction} />
          <DialogContent sx={smallDialogBodySx}>
            {modalConfig.icon === 'none' ? (
              <PlainMessage>{modalConfig.content}</PlainMessage>
            ) : (
              <DialogMessage
                tone={
                  modalConfig.tone ?? (modalConfig.danger ? 'danger' : 'warn')
                }
                icon={modalConfig.icon}
              >
                {modalConfig.content}
              </DialogMessage>
            )}
          </DialogContent>
          <DialogActionsContainer>
            <ConfirmDialogButtons
              cancelLabel={ActionLabels.Cancel}
              confirmLabel={modalConfig.confirmLabel ?? ActionLabels.Continue}
              danger={modalConfig.danger}
              onCancel={onCancelAction}
              onConfirm={handleOkAction}
            />
          </DialogActionsContainer>
        </Dialog>
      );
    }

    return (
      <Dialog open={open} fullWidth>
        <DialogHeader title={modalConfig.title} onClose={onCancelAction} />
        <DialogContent sx={{ mt: 6 }}>{modalConfig.content}</DialogContent>
        <DialogActionsContainer>
          <Button variant="outlined" onClick={onCancelAction}>
            {ActionLabels.Cancel}
          </Button>
          <Button variant="contained" onClick={handleOkAction}>
            {ActionLabels.Continue}
          </Button>
        </DialogActionsContainer>
      </Dialog>
    );
  }, [open, modalConfig, onCancelAction, handleOkAction, redesign]);

  return {
    modal,
    openModal: useCallback(
      (config: ConfirmationModalConfig): Promise<void> => {
        return new Promise((resolve, reject) => {
          setModalConfig(config);
          setResolveReject({ resolve, reject });
          openModal();
        });
      },
      [openModal]
    ),
  };
};

export default useConfirmationModal;
